import React, { useState, useEffect } from 'react';
import { Controller } from 'react-hook-form';
import api from '../../utils/api';
import VECIErrorMessage from './VECIErrorMessage';
import { toTitleCase } from '../../utils/textFormat';

const VECIPesaing = ({ register, errors, watch, setValue, control, getValues }) => {
  const [provinces, setProvinces] = useState([]);
  const [regencies, setRegencies] = useState({}); // Map provinceName to regencies array
  const [districts, setDistricts] = useState({}); // Map regencyName to districts array
  const [villages, setVillages] = useState({}); // Map districtName to villages array

  useEffect(() => {
    const fetchProvinces = async () => {
      try {
        const data = await api.getProvincies();
        setProvinces(data);
      } catch (error) {
        console.error('Failed to fetch provinces', error);
      }
    };
    fetchProvinces();
  }, []);

  const competitors = watch('competitors') || [];

  // Watch all provinces to trigger fetching regencies when needed
  const watchedProvinces = watch(
    competitors.map((_, i) => `competitors.${i}.province`),
  );

  // Watch all regencies to trigger fetching districts when needed
  const watchedRegencies = watch(
    competitors.map((_, i) => `competitors.${i}.regency`),
  );

  // Watch all subDistricts to trigger fetching villages when needed
  const watchedSubDistricts = watch(
    competitors.map((_, i) => `competitors.${i}.subDistrict`),
  );

  useEffect(() => {
    watchedProvinces.forEach((provinceName, idx) => {
      if (provinceName && !regencies[provinceName]) {
        const fetchRegencies = async () => {
          try {
            const province = provinces.find(
              (p) => p.provinceName === provinceName,
            );
            if (province) {
              const data = await api.getRegenciesByProvincies({
                id: province.provinceId,
              });
              setRegencies((prev) => ({ ...prev, [provinceName]: data }));
            }
          } catch (error) {
            console.error('Failed to fetch regencies', error);
          }
        };
        fetchRegencies();
      }
    });
  }, [watchedProvinces, provinces, regencies]);

  useEffect(() => {
    watchedRegencies.forEach((regencyName, idx) => {
      const provinceName = watchedProvinces[idx];
      if (provinceName && regencyName && !districts[regencyName]) {
        const fetchDistricts = async () => {
          try {
            const regencyList = regencies[provinceName];
            const regency = regencyList?.find(
              (r) => r.regenciesName === regencyName,
            );
            if (regency) {
              const data = await api.getDistrictsByRegencies({
                id: regency.regenciesId,
              });
              setDistricts((prev) => ({ ...prev, [regencyName]: data }));
            }
          } catch (error) {
            console.error('Failed to fetch districts', error);
          }
        };
        fetchDistricts();
      }
    });
  }, [watchedRegencies, watchedProvinces, regencies, districts]);

  useEffect(() => {
    watchedSubDistricts.forEach((districtName, idx) => {
      if (districtName && !villages[districtName]) {
        const fetchVillages = async () => {
          try {
            const regencyName = watchedRegencies[idx];
            const districtList = districts[regencyName];
            const district = districtList?.find(
              (d) => d.districtsName === districtName,
            );
            if (district) {
              const data = await api.getVillagesByDistricts({
                id: district.districtsId,
              });
              setVillages((prev) => ({ ...prev, [districtName]: data }));
            }
          } catch (error) {
            console.error('Failed to fetch villages', error);
          }
        };
        fetchVillages();
      }
    });
  }, [watchedSubDistricts, watchedRegencies, districts, villages]);

  const addCompetitor = () =>
    competitors.length < 5 &&
    setValue('competitors', [
      ...competitors,
      {
        name: '',
        village: '',
        subDistrict: '',
        regency: '',
        province: '',
        shortProfile: '',
        reasons: [],
        otherReason: '',
        isStrongest: competitors.length === 0,
      },
    ]);

  return (
    <div className='card border-0 shadow-sm rounded-4 p-4 p-md-5'>
      <div className='d-flex align-items-center mb-4 border-bottom pb-3'>
        <div className='bg-primary bg-opacity-10 p-3 rounded-4 me-3'>
          <i className='bi bi-people text-primary fs-3'></i>
        </div>
        <h4 className='fw-bold mb-0'>Profiling Pesaing</h4>
        <button
          type='button'
          className='btn btn-sm btn-primary rounded-pill px-3 ms-auto'
          onClick={addCompetitor}
          disabled={competitors.length >= 5}
        >
          <i className='bi bi-plus-lg'></i> Tambah
        </button>
      </div>
      <div className='vstack gap-4'>
        {competitors.map((comp, idx) => (
          <div
            key={idx}
            className={`card border-2 rounded-4 p-4 ${comp.isStrongest ? 'border-primary bg-primary bg-opacity-10 shadow-sm' : 'bg-light'}`}
          >
            <div className='d-flex justify-content-between align-items-start mb-3'>
              <div className='flex-grow-1'>
                <label className='form-label small fw-bold mb-1'>
                  Nama Usaha Pesaing
                </label>
                <input
                  {...register(`competitors.${idx}.name`, {
                    required: 'Nama pesaing wajib diisi',
                    onBlur: (e) =>
                      setValue(
                        `competitors.${idx}.name`,
                        toTitleCase(e.target.value),
                      ),
                  })}
                  className={`form-control border-2 ${errors?.competitors?.[idx]?.name ? 'is-invalid border-danger' : ''}`}
                  placeholder='Contoh: PT. Maju Jaya'
                />
                <VECIErrorMessage error={errors?.competitors?.[idx]?.name} />
              </div>
              <div className='d-flex align-items-center gap-2 ms-3 pt-4'>
                <div
                  className={`form-check form-switch p-1 px-3 m-0 d-flex align-items-center gap-2 rounded-pill border transition-all ${comp.isStrongest ? 'bg-primary bg-opacity-10 border-primary border-opacity-25' : 'bg-white'}`}
                  style={{ cursor: 'pointer', minHeight: '38px' }}
                  onClick={() => {
                    if (!comp.isStrongest) {
                      competitors.forEach((_, i) =>
                        setValue(`competitors.${i}.isStrongest`, i === idx),
                      );
                    }
                  }}
                >
                  <input
                    className='form-check-input m-0'
                    type='checkbox'
                    role='switch'
                    checked={comp.isStrongest}
                    readOnly
                    style={{ cursor: 'pointer' }}
                  />
                  <label
                    className={`form-check-label extra-small fw-bold mb-0 ${comp.isStrongest ? 'text-primary' : 'text-muted'}`}
                    style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    {comp.isStrongest ? 'TERKUAT' : 'SET TERKUAT'}
                  </label>
                </div>
                {competitors.length > 1 && (
                  <button
                    type='button'
                    className='btn btn-outline-danger border-0 p-0 d-flex align-items-center justify-content-center rounded-circle'
                    style={{ width: '38px', height: '38px' }}
                    onClick={() => {
                      const newCompetitors = competitors.filter(
                        (_, i) => i !== idx,
                      );
                      // If we deleted the strongest one and there are still others, pick the first one as strongest
                      if (comp.isStrongest && newCompetitors.length > 0) {
                        newCompetitors[0].isStrongest = true;
                      }
                      setValue('competitors', newCompetitors);
                    }}
                    title='Hapus Pesaing'
                  >
                    <i className='bi bi-trash fs-5'></i>
                  </button>
                )}
              </div>
            </div>

            <label className='form-label small fw-bold mb-1'>
              Lokasi Pesaing
            </label>
            <div className='row g-2 mb-3'>
              <div className='col-md-3 col-6'>
                <Controller
                  name={`competitors.${idx}.province`}
                  control={control}
                  rules={{ required: 'Pilih Provinsi' }}
                  render={({ field }) => (
                    <select
                      {...field}
                      className={`form-select form-select-sm border-2 ${errors?.competitors?.[idx]?.province ? 'is-invalid border-danger' : ''}`}
                      onChange={(e) => {
                        field.onChange(e);
                        // Reset regency, subDistrict, and village when province changes
                        setValue(`competitors.${idx}.regency`, '');
                        setValue(`competitors.${idx}.subDistrict`, '');
                        setValue(`competitors.${idx}.village`, '');
                      }}
                    >
                      <option value=''>Pilih Provinsi...</option>
                      {provinces.map((prov) => (
                        <option
                          key={prov.provinceId}
                          value={prov.provinceName}
                        >
                          {prov.provinceName}
                        </option>
                      ))}
                    </select>
                  )}
                />
              </div>
              <div className='col-md-3 col-6'>
                <Controller
                  name={`competitors.${idx}.regency`}
                  control={control}
                  rules={{ required: 'Pilih Kabupaten/Kota' }}
                  render={({ field }) => (
                    <select
                      {...field}
                      className={`form-select form-select-sm border-2 ${errors?.competitors?.[idx]?.regency ? 'is-invalid border-danger' : ''}`}
                      disabled={!watch(`competitors.${idx}.province`)}
                      onChange={(e) => {
                        field.onChange(e);
                        // Reset subDistrict and village when regency changes
                        setValue(`competitors.${idx}.subDistrict`, '');
                        setValue(`competitors.${idx}.village`, '');
                      }}
                    >
                      <option value=''>Pilih Kabupaten...</option>
                      {regencies[watch(`competitors.${idx}.province`)]?.map(
                        (regency) => (
                          <option
                            key={regency.regenciesId}
                            value={regency.regenciesName}
                          >
                            {regency.regenciesName}
                          </option>
                        ),
                      )}
                    </select>
                  )}
                />
              </div>
              <div className='col-md-3 col-6'>
                <Controller
                  name={`competitors.${idx}.subDistrict`}
                  control={control}
                  rules={{ required: 'Pilih Kecamatan' }}
                  render={({ field }) => (
                    <select
                      {...field}
                      className={`form-select form-select-sm border-2 ${errors?.competitors?.[idx]?.subDistrict ? 'is-invalid border-danger' : ''}`}
                      disabled={!watch(`competitors.${idx}.regency`)}
                      onChange={(e) => {
                        field.onChange(e);
                        // Reset village when subDistrict changes
                        setValue(`competitors.${idx}.village`, '');
                      }}
                    >
                      <option value=''>Pilih Kecamatan...</option>
                      {districts[watch(`competitors.${idx}.regency`)]?.map(
                        (district) => (
                          <option
                            key={district.districtsId}
                            value={district.districtsName}
                          >
                            {district.districtsName}
                          </option>
                        ),
                      )}
                    </select>
                  )}
                />
              </div>
              <div className='col-md-3 col-6'>
                <Controller
                  name={`competitors.${idx}.village`}
                  control={control}
                  rules={{ required: 'Pilih Desa/Kelurahan' }}
                  render={({ field }) => (
                    <select
                      {...field}
                      className={`form-select form-select-sm border-2 ${errors?.competitors?.[idx]?.village ? 'is-invalid border-danger' : ''}`}
                      disabled={!watch(`competitors.${idx}.subDistrict`)}
                    >
                      <option value=''>Pilih Desa...</option>
                      {villages[watch(`competitors.${idx}.subDistrict`)]?.map(
                        (village) => (
                          <option
                            key={village.villagesId}
                            value={village.villagesName}
                          >
                            {village.villagesName}
                          </option>
                        ),
                      )}
                    </select>
                  )}
                />
              </div>

              <div className='col-12'>
                {(errors?.competitors?.[idx]?.village ||
                  errors?.competitors?.[idx]?.subDistrict ||
                  errors?.competitors?.[idx]?.regency ||
                  errors?.competitors?.[idx]?.province) && (
                  <div className='text-danger extra-small'>
                    <i className='bi bi-exclamation-circle me-1'></i> Alamat
                    pesaing belum lengkap
                  </div>
                )}
              </div>
            </div>

            <div className='mb-3'>
              <label className='form-label small fw-bold'>Profil Singkat</label>
              <textarea
                {...register(`competitors.${idx}.shortProfile`, {
                  required:
                    'Berikan deskripsi singkat sektor usaha kompetitor ini',
                })}
                className={`form-control border-2 ${errors?.competitors?.[idx]?.shortProfile ? 'is-invalid border-danger' : ''}`}
                placeholder='Sebutkan sektor dan produk utamanya...'
                rows='2'
              ></textarea>
              <VECIErrorMessage
                error={errors?.competitors?.[idx]?.shortProfile}
              />
            </div>

            {comp.isStrongest && (
              <div className='mt-3 bg-white p-4 rounded-4 border-start border-4 border-primary shadow-sm'>
                <label className='form-label fw-bold small text-primary mb-3 d-flex align-items-center'>
                  <i className='bi bi-info-circle me-2'></i> ALASAN MENGAPA
                  DIANGGAP PALING KUAT (Multi-pilih):
                </label>
                <div className='vstack gap-2'>
                  {[
                    'Target pasar yang sama (lingkup provinsi yang sama)',
                    'Bersaing menekan harga produksi',
                    'Persaingan dalam mendapatkan input/bahan baku',
                    'Bersaing merebut pelanggan yang sama',
                    'Alasan lain yang disebutkan informan',
                  ].map((reason, rIdx) => {
                    const groupName = `competitors.${idx}.reasons`;
                    const currentReasons = watch(groupName) || [];
                    return (
                      <div
                        key={rIdx}
                        className='form-check'
                      >
                        <input
                          type='checkbox'
                          className='form-check-input'
                          id={`reason-${idx}-${rIdx}`}
                          value={reason}
                          checked={currentReasons.includes(reason)}
                          onChange={(e) => {
                            if (e.target.checked)
                              setValue(groupName, [...currentReasons, reason]);
                            else
                              setValue(
                                groupName,
                                currentReasons.filter((r) => r !== reason),
                              );
                          }}
                        />
                        <label
                          className='form-check-label small user-select-none'
                          htmlFor={`reason-${idx}-${rIdx}`}
                        >
                          {reason}
                        </label>
                      </div>
                    );
                  })}
                  {watch(`competitors.${idx}.reasons`)?.includes(
                    'Alasan lain yang disebutkan informan',
                  ) && (
                    <div className='mt-2 animate-fade-in'>
                      <input
                        {...register(`competitors.${idx}.otherReason`)}
                        className='form-control form-control-sm border-2'
                        placeholder='Tuliskan alasan spesifik lainnya di sini...'
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {/* Section N - Daya Saing (Competitive Context) */}
      <div className='mt-5 pt-4 border-top'>
        <div className='d-flex align-items-center mb-4'>
          <div className='bg-warning bg-opacity-10 p-3 rounded-4 me-3'>
            <i className='bi bi-shield-exclamation text-warning fs-3'></i>
          </div>
          <div>
            <h4 className='fw-bold mb-0'>Konteks Kompetitif</h4>
            <p className='text-muted small mb-0'>
              Data pendukung analisis BMC untuk Daya Saing
            </p>
          </div>
        </div>

        {[
          {
            id: 'N1',
            label: 'Apakah ada ancaman produk pengganti? Sebutkan.',
          },
          { id: 'N2', label: 'Apakah ada ancaman pesaing? Sebutkan.' },
          { id: 'N3', label: 'Apakah ada ancaman pendatang baru? Sebutkan.' },
          {
            id: 'N4',
            label: 'Apakah ada ancaman daya tawar pemasok? Sebutkan.',
          },
          {
            id: 'N5',
            label: 'Apakah ada ancaman daya tawar konsumen? Sebutkan.',
          },
        ].map((q) => {
          const hasThreat = watch(`competitiveContext.${q.id}.hasThreat`);
          return (
            <div
              key={q.id}
              className='card border-0 bg-light rounded-4 p-4 mb-3'
            >
              <label className='form-label fw-bold mb-3 d-block'>
                {q.label}
              </label>

              <div className='d-flex gap-4 mb-3'>
                <div className='form-check'>
                  <input
                    className='form-check-input'
                    type='radio'
                    id={`${q.id}_ya`}
                    value='Ya'
                    {...register(`competitiveContext.${q.id}.hasThreat`, {
                      required: 'Pilih salah satu jawaban',
                    })}
                  />
                  <label
                    className='form-check-label fw-semibold'
                    htmlFor={`${q.id}_ya`}
                  >
                    Ada Ancaman
                  </label>
                </div>
                <div className='form-check'>
                  <input
                    className='form-check-input'
                    type='radio'
                    id={`${q.id}_tidak`}
                    value='Tidak'
                    {...register(`competitiveContext.${q.id}.hasThreat`, {
                      required: 'Pilih salah satu jawaban',
                      onChange: () => {
                        setValue(`competitiveContext.${q.id}.description`, '');
                      },
                    })}
                  />
                  <label
                    className='form-check-label fw-semibold'
                    htmlFor={`${q.id}_tidak`}
                  >
                    Tidak Ada
                  </label>
                </div>
              </div>

              {hasThreat === 'Ya' && (
                <div className='animate-fade-in'>
                  <label className='form-label small fw-bold text-primary mb-2'>
                    Sebutkan detail ancaman: <span className='text-danger'>*</span>
                  </label>
                  <textarea
                    {...register(`competitiveContext.${q.id}.description`, {
                      validate: (value) => {
                        const currentHasThreat = watch(`competitiveContext.${q.id}.hasThreat`);
                        if (currentHasThreat === 'Ya' && (!value || value.trim() === '')) {
                          return 'Berikan deskripsi ancaman yang teridentifikasi';
                        }
                        return true;
                      },
                    })}
                    className={`form-control border-2 rounded-3 ${
                      errors?.competitiveContext?.[q.id]?.description ? 'is-invalid border-danger' : ''
                    }`}
                    rows='3'
                    placeholder='Jelaskan secara singkat ancaman yang Anda temukan...'
                  ></textarea>
                </div>
              )}
              <VECIErrorMessage
                error={errors?.competitiveContext?.[q.id]?.hasThreat}
              />
              <VECIErrorMessage
                error={errors?.competitiveContext?.[q.id]?.description}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VECIPesaing;
